/* FileCategoriesContext — React context + hook wrapping the SSOT
 * `/api/admin/file-categories` endpoint.  Fetches once per session.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

// Static fallback mirrors `backend/stage5_file_categories.py::CATEGORIES`.
// Used while the first fetch is in flight so the upload form dropdown
// never flashes empty.
const STATIC_FALLBACK = [
    { key: "drawings",                 label: "Drawings",                  admin_selectable: true,  immutable_on_write: false },
    { key: "renders_3d",               label: "3D Renders",                admin_selectable: true,  immutable_on_write: false },
    { key: "cad_bim",                  label: "CAD / BIM Files",           admin_selectable: true,  immutable_on_write: false },
    { key: "surveys",                  label: "Surveys",                   admin_selectable: true,  immutable_on_write: false },
    { key: "reports",                  label: "Reports",                   admin_selectable: true,  immutable_on_write: false },
    { key: "photographs",              label: "Photographs",               admin_selectable: true,  immutable_on_write: false },
    { key: "specifications",           label: "Specifications",            admin_selectable: true,  immutable_on_write: false },
    { key: "agreements_and_contracts", label: "Agreements & Contracts",    admin_selectable: false, immutable_on_write: true },
    { key: "deliverables",             label: "Deliverables",              admin_selectable: false, immutable_on_write: false },
    { key: "client_uploads",           label: "Client Uploads",            admin_selectable: false, immutable_on_write: false },
    { key: "correspondence",           label: "Correspondence",            admin_selectable: true,  immutable_on_write: false },
    { key: "other",                    label: "Other",                     admin_selectable: true,  immutable_on_write: false },
];

const FileCategoriesContext = createContext({
    categories: STATIC_FALLBACK,
    byKey: Object.fromEntries(STATIC_FALLBACK.map((c) => [c.key, c])),
    adminSelectable: STATIC_FALLBACK.filter((c) => c.admin_selectable),
    loading: true,
    error: null,
});

export function FileCategoriesProvider({ children }) {
    const [state, setState] = useState({
        categories: STATIC_FALLBACK,
        byKey: Object.fromEntries(STATIC_FALLBACK.map((c) => [c.key, c])),
        adminSelectable: STATIC_FALLBACK.filter((c) => c.admin_selectable),
        loading: true, error: null,
    });
    useEffect(() => {
        let alive = true;
        api.get("/admin/file-categories")
            .then((r) => {
                if (!alive) return;
                const cats = r.data?.categories || STATIC_FALLBACK;
                setState({
                    categories: cats,
                    byKey: Object.fromEntries(cats.map((c) => [c.key, c])),
                    adminSelectable: cats.filter((c) => c.admin_selectable),
                    loading: false, error: null,
                });
            })
            .catch((err) => {
                if (!alive) return;
                setState((s) => ({ ...s, loading: false,
                                   error: err?.message || "fetch failed" }));
            });
        return () => { alive = false; };
    }, []);
    return (
        <FileCategoriesContext.Provider value={state}>
            {children}
        </FileCategoriesContext.Provider>
    );
}

export function useFileCategories() {
    return useContext(FileCategoriesContext);
}

// Hydration-time migration: given a legacy / free-text category string,
// returns the controlled key it maps to.  Mirrors backend
// `migrate_legacy`.  Used by the Files table to display legacy rows
// consistently during the deploy window (before the retcon runs).
const LEGACY_MAPPING = {
    executed_agreement:                "agreements_and_contracts",
    executed_contract:                 "agreements_and_contracts",
    executed_change_instrument:        "agreements_and_contracts",
    feasibility_property_image:        "photographs",
    feasibility_supporting_information: "reports",
    rfi_response:                      "client_uploads",
    client_response:                   "client_uploads",
};
const _VALID_KEYS = new Set(STATIC_FALLBACK.map((c) => c.key));
export function migrateCategoryLegacy(key) {
    if (!key) return "correspondence";
    if (LEGACY_MAPPING[key]) return LEGACY_MAPPING[key];
    if (_VALID_KEYS.has(key)) return key;
    return "correspondence";
}
